import {
  ActionList, ActionListItem, AlertVariant, Badge, Bullseye, Button, DatePicker, Divider, EmptyState,
  EmptyStateActions, EmptyStateBody, EmptyStateFooter, Icon, MenuToggle, MenuToggleElement,
  PageSection, Pagination, PaginationVariant, SearchInput, Select, SelectOption, Split, SplitItem,
  TextInput, Title, Toolbar, ToolbarContent, ToolbarFilter, ToolbarGroup, ToolbarItem, ToolbarLabel,
  ToolbarLabelGroup, ToolbarToggleGroup, Tooltip
} from '@patternfly/react-core';
import { AngleDownIcon, AngleRightIcon, EyeIcon, FilterIcon, SearchIcon } from '@patternfly/react-icons';
import { InnerScrollContainer, OuterScrollContainer, Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import * as React from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';

import { KlassApi, KlassSearchParams } from '@app/api';
import { PARAMETER, parameterInt } from '@app/store/parameterStore';
import { KLASS_SEARCH_MAX_PAGE_SIZE } from '@app/utils/config';
import { beginLoading, endLoading } from '@app/AppLayout/LoadingContext';
import { useNotification } from '@app/AppLayout/NotificationContext';
import { DataLoadError } from '@app/components/DataLoadError';
import {
  ClassDecisionLabel, ClassLocation, ClassModalityLabel, ClassStatusLabel, ClassTypeLabel,
  InstructorNames, formatStatusText, getKlassLocationText
} from '@app/components/KlassLabels';
import { PageActionsMenu, PageActionsMenuItem } from '@app/components/PageActionsMenu';
import { useEntity } from '@app/hooks/useEntity';
import { useRowRestore } from '@app/hooks/useRowRestore';
import { useTablePreferences } from '@app/hooks/useTablePreferences';
import { KLASS_STATUS, KLASS_TYPE } from '@app/model/Enums';
import { Klass, exportKlassData } from '@app/model/Klass';
import {
  useClientExecutiveStore, useCourseStore, useInstructorStore, useLanguageStore
} from '@app/store/entityStore';
import { ApiError, toApiError } from '@app/utils/ApiError';
import { ExportFormat, exportRecords } from '@app/utils/export';
import {
  dateFormater, ddMMyyyyFormat, ddMMyyyyHHmmTzFormat, ddMMyyyyParse, fromIsoDate, toIsoDate, weekNumber
} from '@app/utils/functions';

/**
 * The Classes History page: the whole class archive, queried straight from
 * GET /class/search on every search.
 *
 * Where the Classes Overview reads the shared Klass store - which by design only holds
 * the last four weeks onward, filtered and paged in the browser - this page holds no
 * cache at all. Every search, page turn and sort is a request, answered by the server
 * and thrown away when the user leaves. That is the point of the page: it is the only
 * way to reach a class that has fallen out of the store's window, and what it shows is
 * always what the database holds right now.
 *
 * The one thing it does keep is the trip out to a class's detail page and back, which is
 * part of the same task - see `snapshot` below.
 *
 * Read only throughout: the single row action is View.
 */

// --- Detail round trip -------------------------------------------------------------

interface HistorySnapshot {
  /** The criteria the rows were fetched with. */
  criteria: HistoryCriteria;
  /** What was in the toolbar, which may have been edited past the last search. */
  draft: HistoryCriteria;
  rows: Klass[];
  page: number;
  hasMore: boolean;
  sortIndex: number;
  sortField: string;
  sortDirection: 'asc' | 'desc';
  selectedRowId: number | null;
}

/**
 * The last search, kept so that History -> a class -> Back returns to the results the
 * user left rather than re-running the query. Written only when a detail page is opened
 * and read only when the user comes back, so an ordinary visit to the page still starts
 * empty.
 *
 * Module memory rather than sessionStorage: a page of hydrated classes is far too large
 * for a 5MB quota, and this must not survive a reload - a reloaded page has to ask the
 * server again.
 */
let snapshot: HistorySnapshot | null = null;

// --- Criteria ----------------------------------------------------------------------

/**
 * What the toolbar collects, in the shape the search endpoint wants: entity ids for the
 * relations, ISO dates, everything as strings so it can come straight out of a form
 * control. Every list matches any of its values, and the lists are combined with `and`.
 */
interface HistoryCriteria {
  type: string[];
  status: string[];
  courseId: string[];
  instructorId: string[];
  languageId: string[];
  clientExecutiveId: string[];
  offeringId: string[];
  requestId: string[];
  /** yyyy-MM-dd. Keeps classes starting on this day or later. */
  startDate: string | null;
  /** yyyy-MM-dd. Keeps classes ending on this day or earlier. */
  endDate: string | null;
  /** ISO week number, 1-53. */
  week: string | null;
  /** Qualifies `week` only; the server defaults it to the current year. */
  year: string | null;
}

const EMPTY_CRITERIA: HistoryCriteria = {
  type: [], status: [], courseId: [], instructorId: [], languageId: [], clientExecutiveId: [],
  offeringId: [], requestId: [], startDate: null, endDate: null, week: null, year: null
};

/** The criteria fields that hold a list of values, i.e. the ones shown as label groups. */
const LIST_FIELDS = [
  'type', 'status', 'courseId', 'instructorId', 'languageId', 'clientExecutiveId',
  'offeringId', 'requestId'
] as const;

type ListField = typeof LIST_FIELDS[number];

/** A year on its own is ignored by the endpoint, so it does not count as a filter. */
const isEmpty = (criteria: HistoryCriteria): boolean =>
  LIST_FIELDS.every(field => criteria[field].length === 0)
  && !criteria.startDate && !criteria.endDate && !criteria.week;

const isValidWeek = (week: string | null): boolean => {
  if (!week) return true;
  const parsed = Number(week);
  return Number.isInteger(parsed) && parsed >= 1 && parsed <= 53;
};

/** Which criteria field each toolbar category edits. Date and Week are pairs of values
 * and are handled on their own. */
const CATEGORY_FIELDS: Record<string, ListField> = {
  'Type': 'type',
  'Status': 'status',
  'Course Name/SKU': 'courseId',
  'Instructor': 'instructorId',
  'Language': 'languageId',
  'Client Executive': 'clientExecutiveId',
  'Offering Id': 'offeringId',
  'Request Id': 'requestId'
};

// --- Sorting -----------------------------------------------------------------------

/**
 * Column index in the header row -> the entity field to sort by. Only the fields
 * KlassSearchFilter allows are here; asking for any other one is a 400, so the columns
 * missing from this map render without a sort arrow.
 *
 * Week has no field of its own - it is derived from the start date, so it sorts by it.
 */
const SORT_FIELDS: Record<number, string> = {
  0: 'startDateTime',  // Week
  2: 'offeringId',
  3: 'startDateTime',
  4: 'endDateTime',
  5: 'type',
  6: 'modality',
  7: 'totalEnrolled',  // Enrollments
  8: 'status',
  19: 'published',
  20: 'released'
};

/** Start Date, descending: the most recent classes first, which is what a history is
 * read for. Also makes paging deterministic - an unordered query paged by offset can
 * repeat or skip rows between requests. */
const DEFAULT_SORT_INDEX = 3;

/** Rows above this are dropped from an export, so a filter that matches most of the
 * archive cannot page the whole table into the browser. */
const EXPORT_MAX_ROWS = 10_000;

const PER_PAGE_OPTIONS = [10, 20, 50, 100].map(rows => ({ title: String(rows), value: rows }));

const ClassesHistory: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const navType = useNavigationType();
  const { addNotification } = useNotification();

  // --- Identify valid restorations ---
  // Only a way back counts: the browser's Back, or the restoreFilters state the layout's
  // Back button and the breadcrumb links carry. A click on History in the nav menu is a
  // plain push, and starts the page empty as it should.
  const isBrowserBack = navType === 'POP' && location.key !== 'default';
  const isBreadcrumb = (location.state as any)?.restoreFilters === true;
  const isReturnTrip = isBrowserBack || isBreadcrumb;

  // Read, do not consume: under StrictMode a lazy initialiser runs twice, and every
  // state below is seeded from this.
  const [restored] = React.useState<HistorySnapshot | null>(() => (isReturnTrip ? snapshot : null));

  React.useEffect(() => {
    // Any arrival that is not a return trip starts from scratch, so the results of the
    // previous one must not sit in memory waiting to be shown again.
    if (!isReturnTrip) snapshot = null;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Dictionaries -----------------------------------------------------------------
  // The endpoint filters relations by id, so the toolbar has to offer names to pick from
  // and send back ids. These are the small, long lived reference stores; the heavy Klass
  // store is deliberately never mounted by this page.
  const { items: courses } = useEntity(useCourseStore);
  const { items: instructors } = useEntity(useInstructorStore);
  const { items: languages } = useEntity(useLanguageStore);
  const { items: clientExecutives } = useEntity(useClientExecutiveStore);

  // --- Search state -----------------------------------------------------------------

  /** Being edited in the toolbar. Nothing is requested while this changes. */
  const [draft, setDraft] = React.useState<HistoryCriteria>(restored?.draft ?? EMPTY_CRITERIA);
  /** What was searched for. Null until the first search, which is the empty page. */
  const [applied, setApplied] = React.useState<HistoryCriteria | null>(restored?.criteria ?? null);

  const [rows, setRows] = React.useState<Klass[]>(restored?.rows ?? []);
  /** The endpoint returns no total, so this is what the pagination's Next depends on. */
  const [hasMore, setHasMore] = React.useState(restored?.hasMore ?? false);
  const [error, setError] = React.useState<ApiError | null>(null);
  /** A search has answered, so an empty table means "nothing matched". */
  const [hasAnswer, setHasAnswer] = React.useState(restored !== null);

  const [page, setPage] = React.useState(restored?.page ?? 1);
  const [sortIndex, setSortIndex] = React.useState(restored?.sortIndex ?? DEFAULT_SORT_INDEX);
  const [sortField, setSortField] = React.useState(restored?.sortField ?? SORT_FIELDS[DEFAULT_SORT_INDEX]!);
  const [sortDirection, setSortDirection] = React.useState<'asc' | 'desc'>(restored?.sortDirection ?? 'desc');
  /** Bumped to re-run the same query, for Refresh and for Try again. */
  const [refreshToken, setRefreshToken] = React.useState(0);

  const [selectedRowId, setSelectedRowId] = React.useState<number | null>(restored?.selectedRowId ?? null);

  // Density and page size, remembered in a cookie across sessions like the other tables.
  const { perPage, setPerPage } = useTablePreferences('classesHistory', 20);
  // Puts the scroll back on the row the user opened on the way back from its detail page.
  const { rememberRow, rowProps } = useRowRestore('classesHistory');

  // --- Toolbar UI state -------------------------------------------------------------
  const [isFiltersExpanded, setIsFiltersExpanded] = React.useState(true);
  const [currentCategory, setCurrentCategory] = React.useState<string>('Date');
  const [isCategoryOpen, setIsCategoryOpen] = React.useState(false);
  const [openFilter, setOpenFilter] = React.useState<string | null>(null);
  const [textInput, setTextInput] = React.useState('');
  const [optionSearch, setOptionSearch] = React.useState('');

  const isFilterOpen = (category: string) => openFilter === category;
  const toggleFilter = (category: string) => {
    setOpenFilter(isFilterOpen(category) ? null : category);
    setOptionSearch('');
  };

  // --- Fetching ---------------------------------------------------------------------

  /** The last request wins: a slow answer to a superseded query must not land. */
  const requestSeq = React.useRef(0);

  /** Everything that decides what a request asks for, as one comparable value. */
  const queryKey = React.useMemo(() => JSON.stringify(
    [applied, page, perPage, sortField, sortDirection, refreshToken]
  ), [applied, page, perPage, sortField, sortDirection, refreshToken]);

  /**
   * The query whose rows are on screen. Comparing against it makes the effect below
   * idempotent, which matters twice over: StrictMode runs it twice on mount in
   * development, and on a return trip the rows are already in hand and must not be
   * fetched again - the whole point of keeping the snapshot.
   */
  const fetchedKey = React.useRef<string | null>(restored ? queryKey : null);

  const toParams = React.useCallback((
    criteria: HistoryCriteria,
    paging: { page: number; size: number }
  ): KlassSearchParams => ({
    type: criteria.type,
    status: criteria.status,
    courseId: criteria.courseId.map(Number),
    instructorId: criteria.instructorId.map(Number),
    languageId: criteria.languageId.map(Number),
    clientExecutiveId: criteria.clientExecutiveId.map(Number),
    offeringId: criteria.offeringId.map(Number),
    requestId: criteria.requestId,
    startDate: criteria.startDate,
    endDate: criteria.endDate,
    week: criteria.week ? Number(criteria.week) : null,
    year: criteria.week && criteria.year ? Number(criteria.year) : null,
    sort: [`${sortDirection === 'desc' ? '-' : ''}${sortField}`],
    ...paging
  }), [sortField, sortDirection]);

  React.useEffect(() => {
    if (!applied) return;
    if (fetchedKey.current === queryKey) return;
    fetchedKey.current = queryKey;

    // Staleness is decided by the sequence number alone, deliberately: dropping the
    // answer in a cleanup would lose it entirely under StrictMode, whose second run is
    // the one the guard above skips.
    const seq = ++requestSeq.current;
    setHasAnswer(false);

    const run = async () => {
      try {
        // One row more than the page fits: the overflow is how a page with no total
        // count knows whether there is a next one.
        const found = await KlassApi.search(toParams(applied, { page: page - 1, size: perPage + 1 }));
        if (seq !== requestSeq.current) return;
        setRows(found.slice(0, perPage));
        setHasMore(found.length > perPage);
        setError(null);
      } catch (failure) {
        if (seq !== requestSeq.current) return;
        setRows([]);
        setHasMore(false);
        setError(toApiError(failure));
      } finally {
        if (seq === requestSeq.current) setHasAnswer(true);
      }
    };

    void run();
  }, [queryKey, applied, page, perPage, toParams]);

  const search = () => {
    const criteria = withPendingText(draft);
    if (isEmpty(criteria) || !isValidWeek(criteria.week)) return;
    // Kept, not just searched for: the value the user typed is now one of the criteria, so it
    // has to show as a chip like every other one, and the box it came from is done with.
    setDraft(criteria);
    setTextInput('');
    setApplied(criteria);
    setPage(1);
    setSelectedRowId(null);
    // Same criteria as last time still means "search again", which is what the button says.
    setRefreshToken(token => token + 1);
  };

  const refresh = () => {
    if (applied) setRefreshToken(token => token + 1);
  };

  // --- Filter handlers --------------------------------------------------------------

  /**
   * Picking a value adds it to whatever the category already holds, and picking one that is
   * already held does nothing. The criteria only ever grow from the controls: the chips below
   * them, and Clear all filters, are the only things that take a value away. So a menu entry
   * cannot be unticked - it is drawn ticked because the criteria hold it, and the chip is what
   * says so.
   */
  const addListValue = (field: ListField, value: string) => {
    setDraft(current => current[field].includes(value)
      ? current
      : { ...current, [field]: [...current[field], value] });
  };

  const addTextValue = (field: ListField, value: string) => {
    const trimmed = value.trim();
    if (!trimmed || draft[field].includes(trimmed)) return;
    setDraft(current => ({ ...current, [field]: [...current[field], trimmed] }));
    setTextInput('');
  };

  /**
   * The same criteria with whatever is still sitting half typed in a text filter added to them.
   *
   * Enter is what normally turns that text into a chip, and without this a value typed and left
   * there is dropped by the search that follows - the toolbar shows an Offering Id and the table
   * answers as though no id had been given. The category alone says which field it belongs to:
   * only the text filters write to `textInput`, and it is emptied whenever the category changes.
   */
  const withPendingText = (criteria: HistoryCriteria): HistoryCriteria => {
    const field = CATEGORY_FIELDS[currentCategory];
    const trimmed = textInput.trim();
    if (!field || !trimmed || criteria[field].includes(trimmed)) return criteria;
    return { ...criteria, [field]: [...criteria[field], trimmed] };
  };

  const onDeleteFilter = (category: string | ToolbarLabelGroup, label: string | ToolbarLabel) => {
    const categoryName = String(typeof category === 'string' ? category : category.name);

    if (categoryName === 'Date') {
      setDraft(current => ({ ...current, startDate: null, endDate: null }));
      return;
    }
    if (categoryName === 'Week') {
      setDraft(current => ({ ...current, week: null, year: null }));
      return;
    }

    const field = CATEGORY_FIELDS[categoryName];
    if (!field) return;
    const value = typeof label === 'string' ? label : String(label.key);
    setDraft(current => ({ ...current, [field]: current[field].filter(entry => entry !== value) }));
  };

  /** Empties the toolbar and puts the page back to its initial "set a filter" state. */
  const clearFilters = () => {
    requestSeq.current++;   // orphans the answer to a search that is still in flight
    setDraft(EMPTY_CRITERIA);
    setApplied(null);
    setRows([]);
    setHasMore(false);
    setError(null);
    setHasAnswer(false);
    setPage(1);
    setSelectedRowId(null);
    setTextInput('');
    setOptionSearch('');
  };

  // --- Label groups -----------------------------------------------------------------
  // Chips carry the id as their key and show the name, so removing one is unambiguous
  // even for two records with the same name.

  const idLabels = <T extends { id: number }>(ids: string[], items: T[], label: (item: T) => string) =>
    ids.map(id => {
      // A dictionary still loading, or a record since removed, leaves the id readable
      // rather than showing an empty or half-built chip.
      const item = items.find(entry => String(entry.id) === id);
      return { key: id, node: item ? label(item) : `#${id}` };
    });

  const dateLabels = draft.startDate || draft.endDate
    ? [`${draft.startDate ? ddMMyyyyFormat(fromIsoDate(draft.startDate)) : 'Any'} to ${draft.endDate ? ddMMyyyyFormat(fromIsoDate(draft.endDate)) : 'Any'}`]
    : [];

  const weekLabels = draft.week
    ? [`Week ${draft.week}${draft.year ? ` of ${draft.year}` : ''}`]
    : [];

  const activeFilterCount = LIST_FIELDS.reduce((total, field) => total + draft[field].length, 0)
    + (draft.startDate || draft.endDate ? 1 : 0)
    + (draft.week ? 1 : 0);

  // --- Export -----------------------------------------------------------------------

  /**
   * Exports every class the current filters match, not the page on screen: pages the
   * endpoint at its maximum size until it runs out. The loading backdrop is held up
   * around the whole run rather than flickering once per request.
   */
  const exportAll = async (exportFormat: ExportFormat) => {
    if (!applied) return;

    beginLoading();
    try {
      const found: Klass[] = [];
      let truncated = false;

      // The largest page the endpoint will honour, asked of the service rather than assumed: it
      // is an admin-adjustable setting, so a constant here would be a copy that goes stale the
      // moment somebody changes it in General Settings. Read once and reused, so a change
      // arriving mid-export cannot alter the size half way through the paging.
      const pageSize = parameterInt(PARAMETER.SEARCH_MAX_PAGE_SIZE, KLASS_SEARCH_MAX_PAGE_SIZE);

      for (let apiPage = 0; ; apiPage++) {
        const batch = await KlassApi.search(toParams(applied, { page: apiPage, size: pageSize }));
        found.push(...batch);

        // Only an empty page ends this, deliberately not a page shorter than the one asked for.
        // The endpoint silently clamps a size above its own maximum, so a short page can equally
        // mean "you asked for more than I allow" — and stopping on that would end the export
        // after the first page and report the partial file as complete. Costs one extra request.
        if (batch.length === 0) break;
        if (found.length >= EXPORT_MAX_ROWS) {
          truncated = true;
          break;
        }
      }

      if (found.length === 0) {
        addNotification('Nothing to export', AlertVariant.warning, 'No class matches the current filters.');
        return;
      }

      const exported = found.slice(0, EXPORT_MAX_ROWS);
      const fileName = exportRecords(exported.map(exportKlassData), 'classes_history', exportFormat, 'Classes History');

      if (truncated) {
        addNotification('Export truncated', AlertVariant.warning,
          `The filters match at least ${EXPORT_MAX_ROWS.toLocaleString()} classes. The first ${exported.length.toLocaleString()} were exported to ${fileName}; narrow the filters for the rest.`);
      } else {
        addNotification('Export ready', AlertVariant.success,
          `${exported.length} class${exported.length === 1 ? '' : 'es'} exported to ${fileName}`);
      }
    } catch (failure) {
      addNotification('Error exporting classes', AlertVariant.danger, toApiError(failure).message);
    } finally {
      endLoading();
    }
  };

  const kebabItems: PageActionsMenuItem[] = [
    { key: 'export-csv', label: 'Export all results to CSV...', onClick: () => exportAll('csv'), isDisabled: !applied },
    { key: 'export-xlsx', label: 'Export all results to Excel...', onClick: () => exportAll('xlsx'), isDisabled: !applied }
  ];

  // --- Row action -------------------------------------------------------------------

  const viewClass = (row: Klass) => {
    setSelectedRowId(row.id);
    // Hand the results to the next mount, so coming back from the detail page does not
    // repeat the query the user has already paid for.
    snapshot = {
      criteria: applied!, draft, rows, page, hasMore,
      sortIndex, sortField, sortDirection, selectedRowId: row.id
    };
    rememberRow(row.id, page);
    // The detail page under this one's path, so Back and the breadcrumb come back here.
    navigate('/classes/history/' + row.offeringId);
  };

  // --- UI render builders -----------------------------------------------------------

  const getSortParams = (columnIndex: number) => {
    if (!SORT_FIELDS[columnIndex]) return undefined;

    return {
      sortBy: { index: sortIndex, direction: sortDirection, defaultDirection: 'asc' as const },
      onSort: (_event: React.MouseEvent, index: number, direction: 'asc' | 'desc') => {
        setSortIndex(index);
        setSortField(SORT_FIELDS[index]!);
        setSortDirection(direction);
        setPage(1);
      },
      columnIndex
    };
  };

  const buildCategoryDropdown = () => (
    <ToolbarItem>
      <Select
        onSelect={(_event, value) => { setCurrentCategory(value as string); setIsCategoryOpen(false); setTextInput(''); }}
        selected={currentCategory} isOpen={isCategoryOpen} onOpenChange={setIsCategoryOpen}
        toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
          <MenuToggle ref={toggleRef} onClick={() => setIsCategoryOpen(!isCategoryOpen)} isExpanded={isCategoryOpen}
            icon={<FilterIcon />} style={{ width: '100%', verticalAlign: 'text-bottom' }}>
            {currentCategory}
          </MenuToggle>
        )}
      >
        <SelectOption value="Date">Date</SelectOption>
        <SelectOption value="Week">Week</SelectOption>
        <SelectOption value="Type">Type</SelectOption>
        <SelectOption value="Status">Status</SelectOption>
        <SelectOption value="Course Name/SKU">Course Name/SKU</SelectOption>
        <SelectOption value="Instructor">Instructor</SelectOption>
        <SelectOption value="Language">Language</SelectOption>
        <SelectOption value="Client Executive">Client Executive</SelectOption>
        <SelectOption value="Offering Id">Offering Id</SelectOption>
        <SelectOption value="Request Id">Request Id</SelectOption>
      </Select>
    </ToolbarItem>
  );

  /** A searchable checkbox Select over one of the dictionaries, sending back ids. */
  const buildOptionFilter = <T extends { id: number }>(
    category: string,
    field: ListField,
    items: T[],
    label: (item: T) => string,
    options?: { placeholder?: string; minWidth?: string }
  ) => {
    const matching = items.filter(item => label(item).toLowerCase().includes(optionSearch.toLowerCase()));

    return (
      <ToolbarFilter
        labels={idLabels(draft[field], items, label)}
        deleteLabel={onDeleteFilter}
        categoryName={category}
        showToolbarItem={currentCategory === category}
      >
        <Select
          aria-label={category} isOpen={isFilterOpen(category)}
          onOpenChange={(isOpen) => setOpenFilter(isOpen ? category : null)}
          popperProps={{ minWidth: options?.minWidth ?? '200px' }}
          selected={draft[field]}
          onSelect={(_event, value) => addListValue(field, String(value))}
          toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
            <MenuToggle ref={toggleRef} onClick={() => toggleFilter(category)} isExpanded={isFilterOpen(category)}
              style={{ width: '100%', verticalAlign: 'text-bottom' }}>
              {options?.placeholder ?? `Filter by ${category.toLowerCase()}`}
              {draft[field].length > 0 && <> <Badge isRead>{draft[field].length}</Badge></>}
            </MenuToggle>
          )}
        >
          <div style={{ padding: '8px' }}>
            <SearchInput value={optionSearch} onChange={(_event, value) => setOptionSearch(value)}
              onClear={() => setOptionSearch('')} placeholder={`Search ${category.toLowerCase()}...`}
              aria-label={`Search ${category}`} />
          </div>
          <Divider />
          {matching.map(item => (
            <SelectOption hasCheckbox key={item.id} value={String(item.id)}
              isSelected={draft[field].includes(String(item.id))}>
              {label(item)}
            </SelectOption>
          ))}
          {matching.length === 0 && <SelectOption isDisabled value="no-results">No results found</SelectOption>}
        </Select>
      </ToolbarFilter>
    );
  };

  /** A free text filter, each entry added as its own value. */
  const buildTextFilter = (category: string, field: ListField, options?: { digitsOnly?: boolean; hint?: string }) => (
    <ToolbarFilter
      labels={draft[field]}
      deleteLabel={onDeleteFilter}
      categoryName={category}
      showToolbarItem={currentCategory === category}
    >
      <SearchInput
        aria-label={`${category} filter`}
        placeholder={options?.hint ?? `Filter by ${category}...`}
        value={textInput}
        onChange={(_event, value) => setTextInput(options?.digitsOnly ? value.replace(/\D/g, '') : value)}
        onClear={() => setTextInput('')}
        onSearch={(_event, value) => addTextValue(field, value)}
      />
    </ToolbarFilter>
  );

  const buildEnumFilter = (category: string, field: ListField, values: string[]) => (
    <ToolbarFilter
      labels={draft[field].map(value => ({ key: value, node: formatStatusText(value) }))}
      deleteLabel={onDeleteFilter}
      categoryName={category}
      showToolbarItem={currentCategory === category}
    >
      <Select
        aria-label={category} isOpen={isFilterOpen(category)}
        onOpenChange={(isOpen) => setOpenFilter(isOpen ? category : null)}
        popperProps={{ minWidth: '180px' }}
        selected={draft[field]}
        onSelect={(_event, value) => addListValue(field, String(value))}
        toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
          <MenuToggle ref={toggleRef} onClick={() => toggleFilter(category)} isExpanded={isFilterOpen(category)}
            style={{ width: '100%', verticalAlign: 'text-bottom' }}>
            {`Filter by ${category.toLowerCase()}`}
            {draft[field].length > 0 && <> <Badge isRead>{draft[field].length}</Badge></>}
          </MenuToggle>
        )}
      >
        {values.map(value => (
          <SelectOption hasCheckbox key={value} value={value} isSelected={draft[field].includes(value)}>
            {formatStatusText(value)}
          </SelectOption>
        ))}
      </Select>
    </ToolbarFilter>
  );

  /** No lower bound here, unlike the Overview: reaching back past its four week window is
   * the whole purpose of this page. */
  const buildDateFilter = () => (
    <ToolbarFilter labels={dateLabels} deleteLabel={onDeleteFilter} categoryName="Date"
      showToolbarItem={currentCategory === 'Date'}>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <DatePicker
          value={draft.startDate ? dateFormater(fromIsoDate(draft.startDate)) : ''}
          onChange={(_event, _value, date) => setDraft(current => ({
            ...current, startDate: date && !isNaN(date.getTime()) ? toIsoDate(date) : null
          }))}
          aria-label="Start date" dateFormat={dateFormater} dateParse={ddMMyyyyParse} placeholder="DD/MM/YYYY"
        />
        <span>to</span>
        <DatePicker
          value={draft.endDate ? dateFormater(fromIsoDate(draft.endDate)) : ''}
          onChange={(_event, _value, date) => setDraft(current => ({
            ...current, endDate: date && !isNaN(date.getTime()) ? toIsoDate(date) : null
          }))}
          aria-label="End date" dateFormat={dateFormater} dateParse={ddMMyyyyParse} placeholder="DD/MM/YYYY"
        />
        <Tooltip content="Classes that start on or after the first date and end on or before the second.">
          <span className="tooltipped-text">Range</span>
        </Tooltip>
      </div>
    </ToolbarFilter>
  );

  const buildWeekFilter = () => (
    <ToolbarFilter labels={weekLabels} deleteLabel={onDeleteFilter} categoryName="Week"
      showToolbarItem={currentCategory === 'Week'}>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <TextInput aria-label="Week number" type="number" min={1} max={53} style={{ width: '110px' }}
          placeholder="Week" value={draft.week ?? ''}
          validated={isValidWeek(draft.week) ? 'default' : 'error'}
          onChange={(_event, value) => setDraft(current => ({ ...current, week: value || null }))}
        />
        <TextInput aria-label="Week year" type="number" style={{ width: '110px' }}
          placeholder={String(new Date().getFullYear())} value={draft.year ?? ''}
          onChange={(_event, value) => setDraft(current => ({ ...current, year: value || null }))}
        />
        <Tooltip content="ISO week, 1 to 53. Matches every class running during that week. Defaults to the current year.">
          <span className="tooltipped-text">ISO week</span>
        </Tooltip>
      </div>
    </ToolbarFilter>
  );

  const buildPagination = (variant: 'bottom' | 'top', isCompact: boolean) => {
    // The endpoint gives no total. Counting the probe row as one more item is what keeps
    // Next enabled while there are further pages, and no more than that.
    const itemCount = (page - 1) * perPage + rows.length + (hasMore ? 1 : 0);

    return (
      <Pagination
        isCompact={isCompact} itemCount={itemCount} page={page} perPage={perPage}
        perPageOptions={PER_PAGE_OPTIONS}
        onSetPage={(_event, newPage) => setPage(newPage)}
        onPerPageSelect={(_event, newPerPage, newPage) => { setPerPage(newPerPage); setPage(newPage); }}
        variant={variant as PaginationVariant} isStatic
        toggleTemplate={({ firstIndex, lastIndex }) => (
          <React.Fragment>
            <b>{firstIndex} - {lastIndex}</b> of {hasMore ? 'many' : itemCount}
          </React.Fragment>
        )}
      />
    );
  };

  const compactHeaderStyle = { paddingLeft: '4px', paddingRight: '4px' };

  // Frozen columns, as on the Overview: the three leading ones pinned left, Actions right.
  // `offset` is the sum of the `width`s before it, so the two must stay in sync.
  const STICKY = {
    week: { width: '70px', offset: '0' },
    sku: { width: '120px', offset: '70px' },
    offeringId: { width: '130px', offset: '190px' },
    actions: { width: '60px' }
  };

  // The space the table gets: the window less the masthead, title, filters and pagination.
  const tableViewportHeight = isFiltersExpanded ? 'calc(100vh - var(--cas-table-offset))' : 'calc(100vh - var(--cas-table-offset-sm))';

  const renderPlaceholder = () => {
    if (error) return <DataLoadError error={error} entityLabel="classes" onRetry={refresh} />;

    // A search is on its way: the app's own backdrop and spinner are over the page, so
    // anything drawn here would only flash "nothing found" at the user on the way past.
    if (applied && !hasAnswer) return null;

    if (!applied) {
      return (
        <Bullseye>
          <EmptyState headingLevel="h5" icon={SearchIcon} titleText="Set a filter to list the classes">
            <EmptyStateBody>
              This page searches the whole class history on the server, so it is not limited to the
              last few weeks like the Classes list. Pick at least one filter and press Search.
            </EmptyStateBody>
          </EmptyState>
        </Bullseye>
      );
    }

    return (
      <Bullseye>
        <EmptyState headingLevel="h5" icon={SearchIcon} titleText="No classes found">
          <EmptyStateBody>No class matches the filter criteria. Change the filters and search again.</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="link" onClick={clearFilters}>Clear all filters</Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      </Bullseye>
    );
  };

  return (
    <React.Fragment>
      <PageSection hasBodyWrapper isWidthLimited>
        <Split>
          <SplitItem>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Button variant="plain" aria-label="Toggle filters" style={{ padding: 0, marginTop: '4px' }}
                onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}>
                <Icon size="xl">{isFiltersExpanded ? <AngleDownIcon /> : <AngleRightIcon />}</Icon>
              </Button>
              <Title headingLevel="h1" size="4xl">Classes History</Title>
              {!isFiltersExpanded && activeFilterCount > 0 && (
                <Badge isRead>{activeFilterCount} Active Filter{activeFilterCount !== 1 ? 's' : ''}</Badge>
              )}
            </div>
          </SplitItem>
          <SplitItem isFilled></SplitItem>
          <SplitItem>
            <ActionList>
              <ActionListItem>
                <PageActionsMenu onRefresh={refresh} items={kebabItems} />
              </ActionListItem>
            </ActionList>
          </SplitItem>
        </Split>
      </PageSection>

      {isFiltersExpanded && (
        <Toolbar id="classes-history-toolbar" clearAllFilters={clearFilters} collapseListedFiltersBreakpoint="xl">
          <ToolbarContent>
            <ToolbarToggleGroup toggleIcon={<FilterIcon />} breakpoint="xl">
              <ToolbarGroup variant="filter-group" style={{ lineHeight: '22px', alignItems: 'center' }}>
                {buildCategoryDropdown()}
                {buildDateFilter()}
                {buildWeekFilter()}
                {buildEnumFilter('Type', 'type', Object.values(KLASS_TYPE))}
                {buildEnumFilter('Status', 'status', Object.values(KLASS_STATUS))}
                {buildOptionFilter('Course Name/SKU', 'courseId', courses,
                  course => `${course.sku} - ${course.name}`, { minWidth: '320px', placeholder: 'Filter by course' })}
                {buildOptionFilter('Instructor', 'instructorId', instructors, instructor => instructor.name)}
                {buildOptionFilter('Language', 'languageId', languages, language => language.englishName)}
                {buildOptionFilter('Client Executive', 'clientExecutiveId', clientExecutives,
                  executive => executive.name, { placeholder: 'Filter by CE' })}
                {buildTextFilter('Offering Id', 'offeringId', { digitsOnly: true })}
                {/* Both private (REQnnnn) and ad hoc (ADRnnnn) classes carry a request id, and the
                    endpoint matches the field on either subtype, so the hint names both forms. */}
                {buildTextFilter('Request Id', 'requestId', { hint: 'Filter by Request Id, REQnnnn or ADRnnnn...' })}
              </ToolbarGroup>
            </ToolbarToggleGroup>
            {/* Outside the toggle group on purpose: below xl that group folds into a
                dropdown, and the button that runs the search has to stay in reach. */}
            <ToolbarItem>
              <Button variant="primary" icon={<SearchIcon />} onClick={search}
                isDisabled={isEmpty(withPendingText(draft)) || !isValidWeek(draft.week)}>
                Search
              </Button>
            </ToolbarItem>
            {applied && !error && <ToolbarItem variant="pagination">{buildPagination('top', true)}</ToolbarItem>}
          </ToolbarContent>
        </Toolbar>
      )}

      {rows.length === 0 ? (
        /* Nothing to draw: stand in for the table and fill the space it would have taken,
           so Bullseye has a definite height to centre the message against. */
        <div style={{ height: tableViewportHeight, overflow: 'auto' }}>
          {renderPlaceholder()}
        </div>
      ) : (
        <OuterScrollContainer style={{ maxHeight: tableViewportHeight, minHeight: 0 }}>
          <InnerScrollContainer>
            {/* gridBreakPoint="" keeps the real table layout at every width, so the frozen
                columns and the horizontal scroll never fall back to stacked cards. */}
            <Table aria-label="Classes history" isStickyHeader variant="compact" gridBreakPoint=""
              className="cas-wide-table">
              <Thead>
                <Tr>
                  <Th sort={getSortParams(0)} style={compactHeaderStyle} modifier="truncate"
                    isStickyColumn stickyMinWidth={STICKY.week.width} stickyLeftOffset={STICKY.week.offset}>Week</Th>
                  <Th style={compactHeaderStyle} modifier="truncate"
                    isStickyColumn stickyMinWidth={STICKY.sku.width} stickyLeftOffset={STICKY.sku.offset}>SKU</Th>
                  <Th sort={getSortParams(2)} style={compactHeaderStyle} modifier="truncate"
                    isStickyColumn stickyMinWidth={STICKY.offeringId.width} stickyLeftOffset={STICKY.offeringId.offset}
                    hasRightBorder>Offering Id</Th>
                  <Th sort={getSortParams(3)} style={compactHeaderStyle}>Start Date</Th>
                  <Th sort={getSortParams(4)} style={compactHeaderStyle}>End Date</Th>
                  <Th sort={getSortParams(5)} style={compactHeaderStyle}>Type</Th>
                  <Th sort={getSortParams(6)} style={compactHeaderStyle}>Modality</Th>
                  <Th sort={getSortParams(7)} style={compactHeaderStyle}>Enrollments</Th>
                  <Th sort={getSortParams(8)} style={compactHeaderStyle}>Status</Th>
                  <Th style={compactHeaderStyle}>Decision</Th>
                  <Th style={compactHeaderStyle}>Instructor</Th>
                  <Th style={compactHeaderStyle}>Allocated Instructor</Th>
                  <Th style={compactHeaderStyle}>Final Instructor</Th>
                  <Th style={compactHeaderStyle}>Instructor Level</Th>
                  <Th style={compactHeaderStyle}>Language</Th>
                  <Th style={compactHeaderStyle}>Location</Th>
                  <Th style={compactHeaderStyle}>Client Executive</Th>
                  <Th style={compactHeaderStyle}>GTR</Th>
                  <Th sort={getSortParams(19)} style={compactHeaderStyle}>Published</Th>
                  <Th sort={getSortParams(20)} style={compactHeaderStyle}>Released</Th>
                  <Th style={compactHeaderStyle}
                    isStickyColumn stickyMinWidth={STICKY.actions.width} stickyRightOffset="0" hasLeftBorder>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {rows.map((row) => (
                  <Tr key={row.id} {...rowProps(row.id)}
                    isSelectable isClickable
                    isRowSelected={selectedRowId === row.id}
                    onRowClick={() => setSelectedRowId(row.id)}>
                    <Td dataLabel="Week" modifier="truncate"
                      isStickyColumn stickyMinWidth={STICKY.week.width} stickyLeftOffset={STICKY.week.offset}>
                      {row.startDateTime ? weekNumber(row.startDateTime) : 'N/A'}
                    </Td>
                    <Td dataLabel="SKU" modifier="truncate"
                      isStickyColumn stickyMinWidth={STICKY.sku.width} stickyLeftOffset={STICKY.sku.offset}>
                      <Tooltip content={row.course?.name}><span className="tooltipped-text-bold">{row.course?.sku}</span></Tooltip>
                    </Td>
                    <Td dataLabel="Offering Id" modifier="truncate"
                      isStickyColumn stickyMinWidth={STICKY.offeringId.width} stickyLeftOffset={STICKY.offeringId.offset}
                      hasRightBorder>
                      {row.offeringId}
                    </Td>
                    <Td dataLabel="Start Date">{row.startDateTime ? ddMMyyyyHHmmTzFormat(row.startDateTime) : 'N/A'}</Td>
                    <Td dataLabel="End Date">{row.endDateTime ? ddMMyyyyHHmmTzFormat(row.endDateTime) : 'N/A'}</Td>
                    <Td dataLabel="Type"><ClassTypeLabel type={row.type} /></Td>
                    <Td dataLabel="Modality"><ClassModalityLabel modality={row.modality} /></Td>
                    <Td dataLabel="Enrollments">
                      <Tooltip
                        position="auto"
                        content={
                          <div style={{ textAlign: 'left', lineHeight: '1.5' }}>
                            <div>Paying: {row.payingDelegates || 0}</div>
                            <div>RHLS Premium: {row.rhlsPremiumDelegates || 0}</div>
                            <div>RHLS Course: {row.rhlsCourseDelegates || 0}</div>
                            <div>Training Units: {row.trainingUnitsDelegates || 0}</div>
                            <div>Free: {row.freeDelegates || 0}</div>
                            <div>Internal: {row.internalDelegates || 0}</div>
                            <div>Bundled: {row.bundledDelegates || 0}</div>
                            <Divider style={{ margin: '8px 0' }} />
                            <div><strong>Booked: {row.delegatesBooked || 0}</strong></div>
                            <div><strong>Total: {row.totalEnrolled || 0}</strong></div>
                            <div><strong>Max: {row.maxDelegates || 0}</strong></div>
                          </div>
                        }
                      >
                        <span className="tooltipped-text">{row.totalEnrolled || 0} / {row.maxDelegates || 0}</span>
                      </Tooltip>
                    </Td>
                    <Td dataLabel="Status"><ClassStatusLabel status={row.status} /></Td>
                    <Td dataLabel="Decision"><ClassDecisionLabel decision={row.decision} /></Td>
                    <Td dataLabel="Instructor"><InstructorNames instructors={row.instructors} /></Td>
                    <Td dataLabel="Allocated Instructor"><InstructorNames instructors={row.allocatedInstructors} /></Td>
                    <Td dataLabel="Final Instructor"><InstructorNames instructors={row.finalInstructors} /></Td>
                    <Td dataLabel="Instructor Level">n/a</Td>
                    <Td dataLabel="Language">{row.language?.englishName}</Td>
                    <Td dataLabel="Location" tooltip={getKlassLocationText(row)}><ClassLocation klass={row} /></Td>
                    <Td dataLabel="Client Executive">{row.clientExecutive?.name}</Td>
                    <Td dataLabel="GTR">{row.guarantedToRun ? 'Yes' : 'No'}</Td>
                    <Td dataLabel="Published">{row.published ? 'Yes' : 'No'}</Td>
                    <Td dataLabel="Released">{row.released ? 'Yes' : 'No'}</Td>
                    <Td dataLabel="Actions" modifier="nowrap"
                      isStickyColumn stickyMinWidth={STICKY.actions.width} stickyRightOffset="0" hasLeftBorder>
                      <Tooltip content="View">
                        <Button
                          icon={<Icon size="lg"><EyeIcon /></Icon>}
                          variant="plain"
                          className="compactButtonStyle"
                          aria-label={`View class ${row.offeringId}`}
                          onClick={() => viewClass(row)}
                        />
                      </Tooltip>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </InnerScrollContainer>
        </OuterScrollContainer>
      )}
      {applied && !error && buildPagination('bottom', false)}
    </React.Fragment>
  );
};

export { ClassesHistory };
